import { Category } from '@firmable/contracts';
import { Constants } from '@firmable/utilities';
import { MRT_ColumnFiltersState, MRT_SortingState } from 'mantine-react-table';
import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { useFilter } from '../Filter';

export type State = {
  pageIndex: Record<Category, number>;
  pageSize: Record<Category, number>;
  sorting: Record<Category, Record<string, 'asc' | 'desc'>>;
  search: Record<Category, Record<string, string>>;
  personPerCompany: number | null;
  optOutEmailSent: boolean;
};

export type Action = {
  setPageIndex: (pageIndex: number) => void;
  setPageSize: (pageSize: number) => void;
  setSorting: (updater: MRT_SortingState) => void;
  setSearch: (filter: MRT_ColumnFiltersState) => void;
  setPersonPerCompany: (value: number | null) => void;
  setOptOutEmailSent: (value: boolean) => void;
};

/**
 * Store to maintain state of search list for both Company and People
 */
const use = create<State & Action>()(
  devtools(
    persist(
      immer((set) => ({
        pageIndex: {
          company: 0,
          people: 0,
        },
        pageSize: {
          company: 10,
          people: 10,
        },
        sorting: {
          company: {},
          people: {},
        },
        search: {
          company: {},
          people: {},
        },
        personPerCompany: null,
        optOutEmailSent: false,
        setPageIndex: (pageIndex: number) =>
          set(
            (state) => {
              const { category } = useFilter.getState();
              state.pageIndex[category] = pageIndex;
              // reset the people sorting when company page index changes
              if (category === 'company') {
                state.pageIndex.people = 0;
              }
            },
            false,
            { type: 'setPageIndex' },
          ),
        setPageSize: (pageSize: number) =>
          set(
            (state) => {
              const { category } = useFilter.getState();
              state.pageSize[category] = pageSize;
            },
            false,
            { type: 'setPageSize' },
          ),
        setSorting: (sortingState: MRT_SortingState) =>
          set(
            (state: State) => {
              const { category } = useFilter.getState();
              state.sorting[category] = sortingState.reduce(
                (acc, { id, desc }) => ({
                  ...acc,
                  [id === 'company-name' || id === 'people-name' ? 'name' : id]: desc ? 'desc' : 'asc',
                  //[id]: desc ? 'desc' : 'asc',
                }),
                {},
              );
            },
            false,
            { type: 'setSorting' },
          ),
        setSearch: (filterState: MRT_ColumnFiltersState) =>
          set(
            (state: State) => {
              const { category } = useFilter.getState();
              state.search[category] = filterState.reduce(
                (acc, { id, value }) => ({
                  ...acc,
                  [id === 'company-name' || id === 'people-name' ? 'name' : id]: value as string,
                  //[id]: value as string,
                }),
                {} as Record<string, string>,
              );
            },
            false,
            { type: 'setFilter' },
          ),
        setPersonPerCompany: (value: number | null) =>
          set(
            (state) => {
              state.personPerCompany = value;
            },
            false,
            { type: 'setPersonPerCompany' },
          ),
        setOptOutEmailSent: (value: boolean) =>
          set(
            (state) => {
              state.optOutEmailSent = value;
            },
            false,
            { type: 'setOptOutEmailSent' },
          ),
      })),
      {
        name: 'mrt-search-list-state',
        version: 2,
        partialize: (state) => ({
          pageIndex: state.pageIndex,
          pageSize: state.pageSize,
          sorting: state.sorting,
          // Exclude search, personPerCompany, and optOutEmailSent from persistence
        }),
        // A page saved before the result window was enforced would be refused by the API on every load.
        merge: (persisted, current) => {
          const merged = { ...current, ...(persisted as Partial<State>) };
          const pageIndex = { ...merged.pageIndex };
          (Object.keys(pageIndex) as Category[]).forEach((category) => {
            if ((pageIndex[category] + 1) * merged.pageSize[category] > Constants.SearchPaging.MAX_RESULT_WINDOW) {
              pageIndex[category] = 0;
            }
          });
          return { ...merged, pageIndex };
        },
      },
    ),
    {
      store: 'SearchListRecordsCompany',
    },
  ),
);

export { use };
